// ./src/components/features/authentication/SigninForm/SigninForm.tsx
import React, { useState } from "react";
import {
  Stack,
  Button,
  TextInput,
  PasswordInput,
  Text,
  Anchor,
  Alert,
  Box,
  Group,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconCheck,
  IconArrowRight,
} from "@tabler/icons-react";

import { useApp } from "@/contexts/AppContext";
import { User } from "@/utils";

export interface SigninFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

export interface SigninFormErrors {
  username?: string;
  password?: string;
  general?: string;
}

export interface SigninFormProps {
  onSigninSuccess?: (userData: User) => void;
  onNavigateToSignup?: () => void;
  onNavigateToForgotPassword?: () => void;
  showSignupLink?: boolean;
}

export const SigninForm: React.FC<SigninFormProps> = ({
  onSigninSuccess,
  onNavigateToSignup,
  onNavigateToForgotPassword,
  showSignupLink = true,
}) => {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<SigninFormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const { signInWithCredentials } = useApp();

  const form = useForm<SigninFormData>({
    initialValues: {
      username: "",
      password: "",
      rememberMe: false,
    },
    validate: {
      username: (value) => (!value ? "Username is required" : null),
      password: (value) => (!value ? "Password is required" : null),
    },
  });

  const handleSubmit = async (values: SigninFormData) => {
    setLoading(true);
    setServerErrors({});
    setSuccessMessage("");

    try {
      const result = await signInWithCredentials(
        values.username,
        values.password
      );

      if (result.didSucceed()) {
        setSuccessMessage("Sign in successful!");
        setTimeout(() => onSigninSuccess?.(result.getDataFirst()), 800);
      } else {
        throw Error(result.getErrorSummary());
      }
    } catch (error) {
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "Sign in failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (successMessage) {
    return (
      <Box
        p="md"
        style={{
          backgroundColor: theme.colors.success[0],
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.success[2]}`,
        }}
      >
        <Group gap="sm" wrap="nowrap">
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: theme.colors.success[5],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconCheck size={20} color="white" />
          </Box>
          <Text size="sm" fw={500} c={theme.colors.success[7]}>
            {successMessage}
          </Text>
        </Group>
      </Box>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        {serverErrors.general && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            radius="md"
            py="xs"
            styles={{
              root: {
                backgroundColor: theme.colors.error[0],
                border: `1px solid ${theme.colors.error[2]}`,
              },
              message: { color: theme.colors.error[7], fontSize: "0.875rem" },
            }}
          >
            {serverErrors.general}
          </Alert>
        )}

        <TextInput
          label="Username"
          placeholder="Enter your username"
          radius="md"
          size="sm"
          {...form.getInputProps("username")}
          error={form.errors.username || serverErrors.username}
          styles={{
            label: { fontSize: "0.8125rem", fontWeight: 500, marginBottom: 4 },
            input: { fontSize: "0.9375rem" },
          }}
        />

        <Box>
          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            radius="md"
            size="sm"
            {...form.getInputProps("password")}
            error={form.errors.password || serverErrors.password}
            styles={{
              label: {
                fontSize: "0.8125rem",
                fontWeight: 500,
                marginBottom: 4,
              },
              input: { fontSize: "0.9375rem" },
            }}
          />
          <Box ta="right" mt={4}>
            <Anchor
              onClick={(e) => {
                e.preventDefault();
                onNavigateToForgotPassword?.();
              }}
              size="xs"
              c={theme.colors.brand[5]}
              fw={500}
            >
              Forgot password?
            </Anchor>
          </Box>
        </Box>

        <Button
          type="submit"
          fullWidth
          size="md"
          radius="md"
          loading={loading}
          rightSection={!loading && <IconArrowRight size={16} />}
          styles={{
            root: {
              height: 44,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.colors.brand[6]} 0%, ${theme.colors.brand[5]} 100%)`,
              "&:hover": { boxShadow: theme.other.shadowBrand },
            },
          }}
        >
          Sign In
        </Button>

        {showSignupLink && (
          <Text size="sm" ta="center" c={theme.colors.neutral[5]} mt="xs">
            Don't have an account?{" "}
            <Anchor
              onClick={(e) => {
                e.preventDefault();
                onNavigateToSignup?.();
              }}
              fw={600}
              c={theme.colors.brand[5]}
            >
              Sign up
            </Anchor>
          </Text>
        )}
      </Stack>
    </form>
  );
};

export default SigninForm;
